// StartFromShapeSheet — Phase 5 Migration B sub-track (c) UI seed (admin).
//
// Secondary entry on the new-invoice authoring panel. Lists saved shapes via
// list_invoice_shapes RPC; tap applies the chosen shape via
// apply_shape_to_invoice_draft and returns the blob to the caller, who
// merges it into authoring draft state. Primary path stays customer-picker-
// first per Q7 lock.

import { useEffect, useState } from 'react';
import { ChevronRight, Layers, X } from 'lucide-react';
import { palette, spacing, typography, transition } from '../../styles/tokens';
import {
  applyShapeToInvoiceDraft,
  listInvoiceShapes,
} from '../../lib/invoiceShapes';
import type {
  InvoiceShape,
  InvoiceShapeApplyBlob,
} from '../../types/invoiceShapes';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onApplied: (blob: InvoiceShapeApplyBlob) => void;
  onError: (message: string) => void;
}

export function StartFromShapeSheet({ isOpen, onClose, onApplied, onError }: Props) {
  const [shapes, setShapes] = useState<InvoiceShape[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [applyingId, setApplyingId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const result = await listInvoiceShapes();
      if (cancelled) return;
      setLoading(false);
      if (!result.success) {
        onError(result.error);
        setShapes([]);
        return;
      }
      setShapes(result.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, onError]);

  if (!isOpen) return null;

  const handleApply = async (shape: InvoiceShape) => {
    if (applyingId !== null) return;
    setApplyingId(shape.id);
    const result = await applyShapeToInvoiceDraft(shape.id);
    setApplyingId(null);
    if (!result.success) {
      onError(result.error);
      return;
    }
    if (!result.data) {
      onError('That shape is no longer available.');
      return;
    }
    onApplied(result.data);
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 70,
          background: 'rgba(0,0,0,0.3)',
          backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        }}
      />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 480, zIndex: 71,
        background: palette.white, borderRadius: 16,
        boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
        padding: 0, maxHeight: '70vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: `1px solid ${palette.g100}`,
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800 }}>
            Start from a saved shape
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: 4, display: 'flex', alignItems: 'center',
          }}>
            <X size={20} color={palette.g500} strokeWidth={2} />
          </button>
        </div>

        <div style={{
          flex: 1, overflowY: 'auto', padding: spacing.md,
        }}>
          {loading ? (
            <div style={{
              padding: spacing.xl, textAlign: 'center',
              fontSize: typography.size.caption, color: palette.g500,
            }}>Loading…</div>
          ) : shapes && shapes.length === 0 ? (
            <div style={{
              padding: spacing.xl, textAlign: 'center',
            }}>
              <div style={{ fontSize: 14, color: palette.g700, marginBottom: 4 }}>
                No saved shapes yet
              </div>
              <div style={{ fontSize: 13, color: palette.g500, lineHeight: 1.5 }}>
                Send an invoice, then choose "Save as a starting point" from its menu.
              </div>
            </div>
          ) : (
            (shapes ?? []).map((shape) => (
              <button
                key={shape.id}
                onClick={() => void handleApply(shape)}
                disabled={applyingId !== null}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: spacing.sm,
                  padding: spacing.md, marginBottom: 4,
                  background: applyingId === shape.id ? palette.g100 : palette.white,
                  border: `1px solid ${palette.g200}`, borderRadius: 12,
                  cursor: applyingId !== null ? 'not-allowed' : 'pointer',
                  textAlign: 'left', fontFamily: 'inherit',
                  transition: `background ${transition.fast}`,
                  opacity: applyingId !== null && applyingId !== shape.id ? 0.6 : 1,
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: palette.blueWash,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Layers size={18} color={palette.blue} strokeWidth={1.8} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 14, fontWeight: 600, color: palette.g800,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{shape.name}</div>
                  <div style={{ fontSize: 12, color: palette.g500, marginTop: 2 }}>
                    {shape.line_items.length}{' '}
                    {shape.line_items.length === 1 ? 'line item' : 'line items'}
                  </div>
                </div>
                <ChevronRight size={16} color={palette.g400} strokeWidth={2} />
              </button>
            ))
          )}
        </div>
      </div>
    </>
  );
}
